import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRemoteVisitorConsentContent } from "../../hooks/Remote/VisitConsent/useRemoteVisitorConsentContent";

export type VisitorConsentContent = {
  title: string;
  subtitle: string;
  description: string;
};

type VisitorConsentContentContextValue = {
  draft: VisitorConsentContent;
  published: VisitorConsentContent;
  isLoading: boolean;
  isPublishing: boolean;
  setDraftField: <K extends keyof VisitorConsentContent>(
    field: K,
    value: VisitorConsentContent[K]
  ) => void;
  publishDraft: () => void;
  resetDraft: () => void;
  applyDraft: (nextDraft: VisitorConsentContent) => void;
  isEmptyDraft: boolean;
};

const VisitorConsentContentContext = createContext<VisitorConsentContentContextValue | null>(null);

const emptyVisitorConsentDraft = (): VisitorConsentContent => ({
  title: "",
  subtitle: "",
  description: ""
});

export const VisitorConsentContentProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { contentQuery, publishMutation } = useRemoteVisitorConsentContent();

  const [draft, setDraft] = useState<VisitorConsentContent>(emptyVisitorConsentDraft);
  const [published, setPublished] = useState<VisitorConsentContent>(emptyVisitorConsentDraft);

  useEffect(() => {
    if (!contentQuery.data) {
      return;
    }
    setDraft(contentQuery.data.draft);
    setPublished(contentQuery.data.published);
  }, [contentQuery.data]);

  const isEmptyDraft = useMemo(
    () =>
      draft.title.trim() === "" && draft.subtitle.trim() === "" && draft.description.trim() === "",
    [draft]
  );

  const publishDraft = useCallback(() => {
    publishMutation.mutate(draft);
  }, [draft, publishMutation]);

  const value = useMemo<VisitorConsentContentContextValue>(
    () => ({
      draft,
      published,
      isLoading: contentQuery.isLoading,
      isPublishing: publishMutation.isLoading,
      setDraftField: (field, nextValue) => {
        setDraft(prev => ({
          ...prev,
          [field]: nextValue
        }));
      },
      publishDraft,
      resetDraft: () => {
        setDraft(emptyVisitorConsentDraft());
      },
      applyDraft: (nextDraft: VisitorConsentContent) => {
        setDraft(nextDraft);
      },
      isEmptyDraft
    }),
    [
      contentQuery.isLoading,
      draft,
      isEmptyDraft,
      publishDraft,
      publishMutation.isLoading,
      published
    ]
  );

  return (
    <VisitorConsentContentContext.Provider value={value}>
      {children}
    </VisitorConsentContentContext.Provider>
  );
};

export const useVisitorConsentContent = () => {
  const context = useContext(VisitorConsentContentContext);
  if (!context) {
    throw new Error("useVisitorConsentContent must be used within VisitorConsentContentProvider");
  }

  return context;
};
